import { type RegisteredApp } from '~/shared/appContract'
import { type AppId, getEnabledApps } from '~/shell/app/registry'
import { switchApp } from '~/shell/app/switchApp'
import { shouldBypassAuthForE2E } from '~/shell/config'

import { sessionClaimsHolder } from './sessionClaims'

/**
 * The Auth0 permission granting access to one product, as a union of the permissions that exist
 * (`'arc:read' | 'scout:read'`).
 *
 * Derived from `AppId` rather than listed, so minting a product's permission is a consequence of
 * registering its app and this type cannot fall behind the suite. The token's `permissions` claim stays
 * a plain `string[]`: it carries whatever Auth0 issued, this is only the subset the switcher asks about.
 */
type ProductPermission = `${AppId}:read`

/**
 * The Auth0 permission granting access to one product.
 *
 * Derived from the product's own key rather than held in a table here, because a Product's key is
 * an "App id" and one permission is minted per product under the same name (`arc` -> `arc:read`).
 * Typed on the ids that exist, so a product whose key names no app is caught here rather than
 * degrading into a permission nothing grants, which would silently withhold the product.
 */
const productPermission = (productId: AppId): ProductPermission =>
  `${productId}:read`

/**
 * The enabled registry apps the current session's product permissions allow it to reach.
 */
export const getEnabledProductApps = (): RegisteredApp<AppId>[] => {
  const enabledApps = getEnabledApps()
  if (shouldBypassAuthForE2E) {
    return enabledApps
  }

  const grantedPermissions = new Set(sessionClaimsHolder.get().permissions)
  return enabledApps.filter((app) =>
    grantedPermissions.has(productPermission(app.id)),
  )
}

export interface Product {
  /**
   * Stable product key; also the React list key. The app's id, so the switcher and the URL cannot
   * disagree about what a product is called.
   */
  id: AppId
  /** Display name shown as the item's bold title (and, for the current product, the switcher trigger label). */
  name: string
  /** One-line description shown under the title in the switcher's detailed item. */
  description: string
  /** Whether this is the product currently being viewed. Drives the switcher's checkmark, nothing else. */
  current: boolean
  /**
   * Resolved navigation target: this product's own app segment under the active customer. Same-origin
   * for every product, since every product the switcher offers is an app this suite serves.
   */
  href: string
}

/**
 * The set of sibling products the current session may reach, driving the header's product switcher.
 *
 * Every product comes from the app registry, so an app's display copy is the app's own (see
 * `shared/appContract`) and adding an app adds a switcher entry with no edit here. Reading
 * `getEnabledApps` rather than the whole registry is what keeps a registered-but-unbuilt app out of the
 * switcher: a stub is mounted so its wiring runs, but it is not somewhere a user is offered. That makes
 * shipping an app a one-line registry change rather than an edit in this file.
 *
 * Every product is therefore an app this suite serves, reached by a same-origin route that keeps the
 * active customer. A product on its own host is no longer a case this hook carries: while Scout was
 * one, the switcher took its target from runtime config, and nothing here names a product or a host
 * any more.
 *
 * What the session may reach is then filtered by its **Product permission**s, read off the access
 * token (Atlas ADR-0022). The permission only ever narrows this list and never sources it, so a grant for
 * a product the suite does not serve is ignored: the registry says what exists, the permission says
 * whether this user may go there.
 *
 * The claims are read synchronously because `RequireAuth` resolves them before any guarded route
 * renders, so this hook has no loading state of its own and the switcher never pops in.
 */
export const useEnabledProducts = (
  pathname: string,
  activeApp: string | undefined,
): Product[] => {
  return getEnabledProductApps().map((app) => ({
    id: app.id,
    name: app.name,
    description: app.description,
    current: app.id === activeApp,
    href: switchApp(pathname, app.id),
  }))
}
